import { useMemo, useState } from "react";
import { useResource } from "@/hooks/useResource";
import { PageHeader } from "@/components/common/PageHeader";
import { LoadingState, ErrorState, EmptyState } from "@/components/common/States";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { apiErrorMessage } from "@/lib/api";
import { fmtCurrency, fmtDate, fmtNumber } from "@/lib/format";
import { Layers, Lock, Search } from "lucide-react";

export default function ProductionBatches() {
  const { data, isLoading, isError, error, refetch } = useResource("/production-batches", { dataPath: "batches" });
  const batches = useMemo(() => (Array.isArray(data) ? data : []), [data]);
  const [selected, setSelected] = useState(null);
  const [q, setQ] = useState("");

  const filtered = useMemo(() => batches.filter((b) => {
    if (!q) return true;
    return `${b.batchNo || ""} ${b.productName || ""}`.toLowerCase().includes(q.toLowerCase());
  }), [batches, q]);

  const consumed = Array.isArray(selected?.consumption) ? selected.consumption
    : Array.isArray(selected?.ingredients) ? selected.ingredients : [];

  return (
    <div className="space-y-6">
      <PageHeader title="Production Runs / Batches" subtitle="Completed production batches with locked (frozen) cost. Later ingredient price changes never alter a batch's historical cost." />

      <div className="flex justify-end">
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
          <Input data-testid="batch-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search batch / product…" className="h-9 pl-8 text-sm" />
        </div>
      </div>

      {isLoading ? <LoadingState label="Loading batches…" /> :
        isError ? <ErrorState message={apiErrorMessage(error)} onRetry={refetch} /> :
        filtered.length === 0 ? <EmptyState title="No production batches yet" message="Batches appear here after a production plan is completed and its cost is locked." /> : (
          <div className="overflow-hidden rounded-xl border border-stone-200 bg-white shadow-sm">
            <table className="w-full text-sm">
              <thead className="border-b border-stone-200 bg-stone-50 text-left text-[11px] font-bold uppercase tracking-wider text-stone-500">
                <tr>
                  <th className="px-4 py-2.5">Batch #</th><th className="px-4 py-2.5">Product</th><th className="px-4 py-2.5">Status</th>
                  <th className="px-4 py-2.5 text-right">Planned</th><th className="px-4 py-2.5 text-right">Produced</th>
                  <th className="px-4 py-2.5 text-right">Standard Cost</th><th className="px-4 py-2.5 text-right">Batch Cost</th>
                  <th className="px-4 py-2.5 text-right">Variance</th><th className="px-4 py-2.5">Mfg Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {filtered.map((b) => (
                  <tr key={b.id || b.batchNo} data-testid={`batch-row-${b.id || b.batchNo}`} className="cursor-pointer hover:bg-amber-50/40" onClick={() => setSelected(b)}>
                    <td className="px-4 py-2.5"><span className="flex items-center gap-2 font-mono text-xs text-stone-700"><Layers className="h-3.5 w-3.5 text-violet-600" />{b.batchNo || (b.id || "").slice(0, 8)}</span></td>
                    <td className="px-4 py-2.5 text-stone-700">{b.productName || "—"}</td>
                    <td className="px-4 py-2.5"><StatusBadge value={b.status} /></td>
                    <td className="px-4 py-2.5 text-right font-mono">{fmtNumber(b.plannedQty)}</td>
                    <td className="px-4 py-2.5 text-right font-mono">{fmtNumber(b.producedQty)}</td>
                    <td className="px-4 py-2.5 text-right font-mono">{fmtCurrency(b.standardTotalCost)}</td>
                    <td className="px-4 py-2.5 text-right font-mono">{fmtCurrency(b.batchCost ?? b.totalCost)}</td>
                    <td className={`px-4 py-2.5 text-right font-mono ${Number(b.costVariance) > 0 ? "text-rose-600" : "text-emerald-700"}`} data-testid={`batch-variance-${b.id || b.batchNo}`}>
                      {b.costVariance != null ? `${Number(b.costVariance) > 0 ? "+" : ""}${fmtCurrency(b.costVariance)}${b.costVariancePct != null ? ` (${Number(b.costVariancePct) > 0 ? "+" : ""}${Number(b.costVariancePct).toFixed(2)}%)` : ""}` : "—"}
                    </td>
                    <td className="px-4 py-2.5">{fmtDate(b.mfgDate || b.date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="w-full overflow-y-auto bg-white sm:max-w-xl">
          {selected && (
            <>
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2 font-display">
                  Batch {selected.batchNo || (selected.id || "").slice(0, 8)} <StatusBadge value={selected.status} />
                </SheetTitle>
                <SheetDescription className="flex items-center gap-1.5 text-xs text-violet-700">
                  <Lock className="h-3.5 w-3.5" /> Cost locked — frozen at production time
                </SheetDescription>
              </SheetHeader>
              <div className="mt-5 space-y-5">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <Field label="Product" value={selected.productName} />
                  <Field label="Recipe version" value={selected.recipeVersion} />
                  <Field label="Planned qty" value={fmtNumber(selected.plannedQty)} />
                  <Field label="Produced qty" value={fmtNumber(selected.producedQty)} />
                  <Field label="Wastage / rejection" value={fmtNumber(selected.wastageQty ?? selected.rejectedQty ?? 0)} />
                  <Field label="Standard (recipe) cost" value={selected.standardTotalCost != null ? fmtCurrency(selected.standardTotalCost) : "—"} />
                  <Field label="Actual batch cost" value={fmtCurrency(selected.batchCost ?? selected.totalCost)} />
                  <Field label="Cost variance" value={selected.costVariance != null ? `${Number(selected.costVariance) > 0 ? "+" : ""}${fmtCurrency(selected.costVariance)}${selected.costVariancePct != null ? ` (${Number(selected.costVariancePct) > 0 ? "+" : ""}${Number(selected.costVariancePct).toFixed(2)}%)` : ""}` : "—"} />
                  <Field label="Actual cost / unit" value={selected.actualUnitCost != null ? fmtCurrency(selected.actualUnitCost) : "—"} />
                  <Field label="Mfg date" value={fmtDate(selected.mfgDate || selected.date)} />
                  <Field label="Expiry / best-before" value={fmtDate(selected.expiryDate)} />
                </div>
                <div className="rounded-lg border border-stone-200">
                  <div className="border-b border-stone-200 bg-stone-50 px-4 py-2 text-sm font-semibold text-stone-800">Ingredient consumption (frozen prices)</div>
                  {consumed.length === 0 ? (
                    <div className="px-4 py-6 text-center text-xs text-muted-foreground">No consumption detail recorded on this batch.</div>
                  ) : (
                    <div className="divide-y divide-stone-100">
                      {consumed.map((c, i) => (
                        <div key={i} className="flex items-center justify-between px-4 py-2 text-xs">
                          <span className="text-stone-700">{c.name}{c.priceSource ? <span className="ml-1 text-stone-400">({c.priceSource})</span> : null}</span>
                          <span className="font-mono">{fmtNumber(c.qty ?? c.quantity, 3)} {c.unit} @ {fmtCurrency(c.rate)} = {fmtCurrency((Number(c.qty ?? c.quantity) || 0) * (Number(c.rate) || 0))}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-stone-400">{label}</div>
      <div className="text-stone-800">{value ?? "—"}</div>
    </div>
  );
}
